/* eslint-disable no-console */
import { AvatarData } from '@components/AvataarsPicker/types'
import { appSessionStorage } from '@utils/storageUtils'
import { message } from 'antd'
import { useCallback, useState } from 'react'

import { Entities_Login_Profile_Response, UserId } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { LoginMode, MetaKey } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'

import {
  convertEnumStringsToNumbers,
  convertEnumValues,
  enumMap
} from '../utils/enumAvatar'

// Helper function to safely check login mode
const isLoginMode = (value: string | undefined, mode: LoginMode): boolean => {
  return value === mode.valueOf()
}

export interface ProfileState {
  loginProfile: Entities_Login_Profile_Response | null
  avatar: AvatarData | null
  initialAvatar: AvatarData | null
  isLoading: boolean
}

interface UpdateParams {
  displayName?: string
  contactName?: string
  avatar?: AvatarData
  loginMode?: LoginMode | string // Allow both enum and string for flexibility
  localStorageLoginMode?: string | null
  userId?: UserId
  contactId?: number
}

interface UseProfileReturn extends ProfileState {
  getLoginProfile: () => Promise<void>
  updateProfile: (updates: UpdateParams) => Promise<boolean>
}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// Global cache to prevent duplicate API calls across components
// Use session storage for persistence across page reloads
const getCachedData = () => {
  try {
    const cached = appSessionStorage.profileCache
    if (cached && Date.now() - cached.lastFetched < CACHE_DURATION) {
      return cached
    }
  } catch (error) {
    // Silent fail - cache will be rebuilt
  }
  return null
}

const setCachedData = (data: { data: ProfileState; lastFetched: number }) => {
  appSessionStorage.profileCache = data
}

const profileCache: {
  data: ProfileState | null
  lastFetched: number
  isLoading: boolean
} = {
  data: getCachedData()?.data || null,
  lastFetched: getCachedData()?.lastFetched || 0,
  isLoading: false
}

export const useProfile = (): UseProfileReturn => {
  const { api } = useFramework()
  const [state, setState] = useState<ProfileState>(() => {
    // Initialize with cached data if available and fresh
    if (
      profileCache.data &&
      Date.now() - profileCache.lastFetched < CACHE_DURATION
    ) {
      return profileCache.data
    }
    return {
      loginProfile: null,
      avatar: null,
      initialAvatar: null,
      isLoading: false
    }
  })

  const { login } = useAuth(isAuthenticated)
  const LOGIN_MODE = 'login_mode'

  const getLoginProfile = useCallback(async () => {
    if (!login.id) return

    // Check if we have fresh cached data
    if (
      profileCache.data &&
      Date.now() - profileCache.lastFetched < CACHE_DURATION
    ) {
      setState(profileCache.data)
      return
    }

    // Prevent duplicate API calls
    if (profileCache.isLoading) {
      return
    }

    profileCache.isLoading = true
    setState((prev) => ({ ...prev, isLoading: true }))

    try {
      // Batch API calls using Promise.all for better performance
      const [profileResponse, avatarResponse] = await Promise.all([
        api.get_Login_Profile({ loginId: login.id }),
        api
          .get_Login_LoginMeta({
            loginId: login.id,
            metaKey: MetaKey.AvatarConfig
          })
          .catch((error) => console.log(error))
      ])

      let finalAvatar: AvatarData | null = null
      let initialAvatar: AvatarData | null = null

      if (avatarResponse?.data) {
        finalAvatar = convertEnumValues(avatarResponse.data, enumMap)
        initialAvatar = { ...finalAvatar }
      }

      const newState: ProfileState = {
        loginProfile: profileResponse,
        avatar: finalAvatar,
        initialAvatar,
        isLoading: false
      }

      // Update cache
      profileCache.data = newState
      profileCache.lastFetched = Date.now()
      profileCache.isLoading = false

      // Persist to session storage
      setCachedData({ data: newState, lastFetched: profileCache.lastFetched })

      setState(newState)
    } catch (error) {
      console.error('Error fetching profile data:', error)
      profileCache.isLoading = false
      setState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [login.id])

  const updateProfile = useCallback(
    async (updates: UpdateParams): Promise<boolean> => {
      if (!state.loginProfile || !login.id) return false

      setState((prev) => ({ ...prev, isLoading: true }))

      try {
        const updatePromises: Promise<unknown>[] = []

        // Display name update
        if (
          updates.displayName !== undefined &&
          state.loginProfile.displayName !== updates.displayName
        ) {
          updatePromises.push(
            api.put_Login_UpdateDisplayName({
              loginId: login.id,
              displayName: updates.displayName
            })
          )
        }

        // Contact name update
        if (
          updates.contactName !== undefined &&
          state.loginProfile.contactName !== updates.contactName &&
          updates.contactId
        ) {
          updatePromises.push(
            api.put_Contact_UpdateName({
              id: updates.contactId,
              name: updates.contactName
            })
          )
        }

        // Avatar update
        if (updates.avatar) {
          console.log('Updating avatar:', updates.avatar)

          const avatarData = convertEnumStringsToNumbers(
            updates.avatar,
            enumMap
          )
          console.log('Converted avatar data:', avatarData)

          updatePromises.push(
            api.post_Login_UpsertLoginMeta({
              loginId: login.id,
              data: avatarData,
              metaKey: MetaKey.AvatarConfig
            })
          )
        }

        // Execute all updates in parallel
        if (updatePromises.length > 0) {
          await Promise.all(updatePromises)
        }

        // Handle login mode changes
        let loginModeChanged = false
        if (updates.loginMode && updates.userId) {
          if (
            updates.localStorageLoginMode === null &&
            isLoginMode(updates.loginMode, LoginMode.Auto)
          ) {
            const response = await api.get_Login_EncryptUserid({
              userId: updates.userId
            })
            if (response.encryptedUserId) {
              localStorage.setItem(LOGIN_MODE, response.encryptedUserId)
              message.success('Login mode set to Auto')
              loginModeChanged = true
            }
          } else if (
            updates.localStorageLoginMode !== null &&
            isLoginMode(updates.loginMode, LoginMode.Manual)
          ) {
            localStorage.removeItem(LOGIN_MODE)
            message.success('Login mode set to Manual')
            loginModeChanged = true
          }
        }

        if (updatePromises.length > 0) {
          message.success('Profile updated successfully')
          // Invalidate cache and refresh data
          profileCache.data = null
          profileCache.lastFetched = 0
          // Clear session storage cache
          sessionStorage.profileCache = null
          await getLoginProfile()
        } else if (loginModeChanged) {
          // Only login mode was changed, no need to refresh profile data
          // The success message was already shown above
        }

        return true
      } catch (error) {
        console.error('Error updating profile:', error)
        message.error('Failed to update profile')
        return false
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }))
      }
    },
    [state.loginProfile, login.id, getLoginProfile]
  )

  return {
    ...state,
    getLoginProfile,
    updateProfile
  }
}
